import {
  AltiusRes,
  DeleteFolderData,
  IFolder,
  IImportTemplateData,
  IMetaDetails,
  IWorkspaceMetadata,
  PatchableMetaDetail,
  UpdateFolderData,
  WorkspaceFilters,
  WorkspaceFiltersFromDB
} from "store/types";

import { DealAction } from "..";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IFolderActions {
  getFoldersAction: (data: { dealId: number; kind?: IFolder["kind"]; disableDispatch?: boolean }) => Promise<IFolder[]>;
  createFolderAction: (data: { dealId?: number; title: string; parent_id?: number | null }) => Promise<IFolder>;
  deleteFolderAction: (data: DeleteFolderData) => Promise<void>;
  updateFolderAction: (data: UpdateFolderData) => Promise<void>;
  invalidateFolderMetaAction: () => void;

  importFolderAction: (data: IImportTemplateData, dealId: number, templateId: number) => Promise<void>;
  getFolderMetadataAction: (data: {
    dealId: number | null;
    sectionId: number | null;
    filters: WorkspaceFilters;
    disableDispatch?: boolean;
    append?: boolean;
    recently_added?: boolean;
    page?: number;
    deal_ids?: number[];
    sort_by?: string;
    sort?: "asc" | "desc";
  }) => Promise<IWorkspaceMetadata>;
  // getFilesMetadataAction: (data: {
  //   dealId: number;
  //   filters: WorkspaceFilters;
  //   recently_added: boolean;
  // }) => Promise<IWorkspaceMetadata>;
  getGlobalWorkspaceFilesAction: (data: {
    filters: WorkspaceFilters;
    recently_added: boolean;
    page?: number;
  }) => Promise<{
    files: IMetaDetails[];
    total: number;
    per_page: number;
    last_page: number;
    current_page: number;
  }>;

  getGlobalFolderFiltersAction: () => Promise<WorkspaceFiltersFromDB>;
  getGlobalWorkspaceFoldersAction: () => Promise<IFolder[]>;
  createGlobalWorkspaceFolderAction: (data: { title: string; parent_id?: number | null }) => Promise<IFolder>;
  patchFolderMetaDetailAction: (
    data:
      | { key: "patch"; id: number; entityType: string; patch: PatchableMetaDetail }
      | { key: "delete"; id: number; entityType: string }
  ) => void;
}

interface FolderActionParams extends AsyncActionsParams {
  isSharedDeal: boolean;
}
export class FolderActions extends AsyncActions implements IFolderActions {
  protected readonly isSharedDeal: boolean;
  public constructor({ isSharedDeal, ...params }: FolderActionParams) {
    super(params);
    this.isSharedDeal = isSharedDeal;
  }

  public createFolderAction: IFolderActions["createFolderAction"] = async ({ dealId, title, parent_id }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFolder>>({
      url: dealId ? `/api/v0.0.2/deals/${dealId}/folders` : "/api/v0.0.3/global-workspace/folders",
      data: {
        title: title,
        parent_id: parent_id
      }
    });

    this.dispatch({ type: DealAction.CreateFolderSuccess, payload: data });
    return data;
  };

  public invalidateFolderMetaAction: IFolderActions["invalidateFolderMetaAction"] = () => {
    this.dispatch({ type: DealAction.InvalidateFolderMeta, payload: null });
  };

  public importFolderAction: IFolderActions["importFolderAction"] = async (sectionsId, dealId, templateId) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFolder[]>>({
      url: `/api/v0.0.2/deals/${dealId}/templates/${templateId}/import`,
      data: sectionsId
    });
    this.dispatch({ type: DealAction.ImportFoldersSuccess, payload: data });
  };

  // New branch structure
  public getFoldersAction: IFolderActions["getFoldersAction"] = async ({
    dealId,
    disableDispatch = false,
    ...params
  }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFolder>>>({
      url: this.isSharedDeal ? `/api/v0.0.3/shared-deals/${dealId}/folders` : `/api/v0.0.3/deals/${dealId}/folders`,
      params
    });
    if (!disableDispatch) {
      this.dispatch({ type: DealAction.GetFoldersSuccess, payload });
    }
    return Object.values(payload);
  };

  // new branch structure
  public updateFolderAction: IFolderActions["updateFolderAction"] = async (data) => {
    const { dealId, folderId, ...body } = data;
    const {
      data: { data: payload }
    } = await this.api.patch<AltiusRes<IFolder>>({
      url: dealId
        ? `/api/v0.0.2/deals/${dealId}/folders/${folderId}`
        : `/api/v0.0.3/global-workspace/folders/${folderId}`,
      data: body
    });
    this.dispatch({ type: DealAction.UpdateFolderSuccess, payload });
  };

  // new branch structure
  public deleteFolderAction: IFolderActions["deleteFolderAction"] = async ({ dealId, folderId }) => {
    await this.api.delete({
      url: dealId
        ? `/api/v0.0.2/deals/${dealId}/folders/${folderId}`
        : `/api/v0.0.3/global-workspace/folders/${folderId}`
    });
    this.dispatch({ type: DealAction.DeleteFolderSuccess, payload: folderId });
  };

  public getFolderMetadataAction = async (data: {
    sectionId: number | null;
    filters: WorkspaceFilters;
    disableDispatch?: boolean;
    append?: boolean;
    recently_added?: boolean;
    dealId: number | null;
    page?: number;
    deal_ids?: number[];
    sort_by?: string;
    sort?: "asc" | "desc";
  }): Promise<IWorkspaceMetadata> => {
    const {
      sectionId,
      filters,
      disableDispatch = false,
      append = false,
      recently_added,
      dealId,
      page,
      deal_ids,
      sort_by,
      sort
    } = data;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<IWorkspaceMetadata>>({
      url: "/api/v0.0.3/workspace-folders",
      data: {
        deal_id: dealId,
        section_id: sectionId,
        ...filters,
        recently_added: recently_added,
        ...(page !== undefined ? { page } : {}),
        ...(deal_ids?.length ? { deal_ids } : {}),
        ...(sort_by ? { sort_by, sort } : {})
      }
    });
    if (!disableDispatch) {
      this.dispatch({
        type: append ? DealAction.AppendFolderMetaDetails : DealAction.GetFolderMetaSuccess,
        payload
      });
    }
    return payload;
  };

  public getGlobalWorkspaceFilesAction = async (data: {
    filters: WorkspaceFilters;
    recently_added: boolean;
    page?: number;
  }): Promise<{
    files: IMetaDetails[];
    total: number;
    per_page: number;
    last_page: number;
    current_page: number;
  }> => {
    const { filters, recently_added, page } = data;
    const {
      data: { data: payload }
    } = await this.api.post<
      AltiusRes<{ files: IMetaDetails[]; total: number; per_page: number; last_page: number; current_page: number }>
    >({
      url: "/api/v0.0.3/global-workspace/files/all",
      data: {
        ...filters,
        recently_added,
        ...(page !== undefined ? { page } : {})
      }
    });
    return payload;
  };

  public getGlobalFolderFiltersAction = async (): Promise<WorkspaceFiltersFromDB> => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<WorkspaceFiltersFromDB>>({
      url: "/api/v0.0.3/global-workspace/workspace-filters"
    });

    return payload;
  };

  public getGlobalWorkspaceFoldersAction: IFolderActions["getGlobalWorkspaceFoldersAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Record<number, IFolder>>>({
      url: "/api/v0.0.3/global-workspace/folders"
    });
    return Object.values(payload);
  };

  public createGlobalWorkspaceFolderAction: IFolderActions["createGlobalWorkspaceFolderAction"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<IFolder>>({
      url: "/api/v0.0.3/global-workspace/folders",
      data: body
    });
    return data;
  };

  public patchFolderMetaDetailAction: IFolderActions["patchFolderMetaDetailAction"] = (data) => {
    this.dispatch({ type: DealAction.PatchFolderMetaDetail, payload: data });
  };
}
